import { BaseDto } from '../base.dto'
import { TranslationDto } from '../translation.dto'

import { ChallengeConfigDto } from './challenge-config.dto'
import { ChallengeMilestoneDto } from './challenge-milestone.dto'
import { ChallengeRewardTierDto } from './challenge-reward.dto'

/**
 * FR-01-BR-02 — trạng thái Challenge.
 * Do BE tính (phụ thuộc thời gian + tổng quà còn lại), FE không tự suy ra.
 * DB không có cột `status` trên bảng `Challenge` → đây là giá trị dẫn xuất.
 *
 * ✅ C35 ĐÃ CHỐT — BE xác nhận đúng 3 chuỗi này (API #9, #10, #11, #12).
 */
export enum ChallengeStatus {
  PLANNED = 'PLANNED',
  ONGOING = 'ONGOING',
  ENDED = 'ENDED'
}

/**
 * API #10/#11/#12 (Swagger 2026-08-25) — BE đã bỏ field `status` đơn, tách thành 2 trục
 * độc lập. Một Challenge có thể `ONGOING` + `HIDDEN` cùng lúc.
 *
 * ✅ ĐÃ CHỐT: UI hiển thị đúng theo mô tả BE — chip chính dùng `timePhase` (4 giá trị, xem
 * `CHALLENGE_TIME_PHASE_LABEL_KEY`/`CHALLENGE_TIME_PHASE_CHIP_CLASS`), badge phụ dùng
 * `lifecycleStatus` chỉ cho `OUT_OF_STOCK`/`DELETED` (xem `CHALLENGE_LIFECYCLE_BADGE_*` —
 * `HIDDEN` đã có toggle Show/Hide riêng, `PUBLISHED` là mặc định không hiện gì).
 * `ChallengeStatus`/`toBridgedStatus()` chỉ còn dùng cho `canDeleteChallenge()` (chỉ cần
 * phân biệt đúng `PLANNED`, không đổi ý nghĩa giữa 2 model) và làm fallback hiển thị khi API
 * #9 (List) chưa xác nhận có trả `timePhase` hay không.
 */
export enum ChallengeTimePhase {
  PLANNED = 'PLANNED',
  ONGOING = 'ONGOING',
  REDEMPTION_ONLY = 'REDEMPTION_ONLY',
  ENDED = 'ENDED'
}

/** Trục thứ 2 — độc lập với `ChallengeTimePhase`. Điều khiển bởi API #13/#14 + hết quà. */
export enum ChallengeLifecycleStatus {
  PUBLISHED = 'PUBLISHED',
  HIDDEN = 'HIDDEN',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  DELETED = 'DELETED'
}

/**
 * API #10/#11/#12 — thay thế `canEdit`/`canDelete` cũ (chỉ có ở API #9 theo `API Design.md`).
 * Chỉ xuất hiện ở response detail; **không có `canDelete`** trong object này — soft-delete
 * (API #14) vẫn trả lỗi 400/403 nếu không hợp lệ, không có cờ báo trước.
 */
export class ChallengeCapabilitiesDto {
  public canJoin?: boolean
  public canIssueStamp?: boolean
  public canClaimReward?: boolean
  public canRedeemReward?: boolean
  /** Gần nhất với `canEdit` cũ — dùng cho nút Edit ở Challenge Detail/List. */
  public canUpdate?: boolean

  public static create(data?: Partial<ChallengeCapabilitiesDto>): ChallengeCapabilitiesDto {
    const instance = new ChallengeCapabilitiesDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * Bảng `ChallengeMall` — Mall áp dụng thử thách.
 *
 * ⚠️ BE trả `mallIds: uuid[]` (API #9, #10), **không trả tên Mall**.
 * `AdminChallengeService` join tên từ `MallPublicService` để dựng lại mảng này.
 */
export class ChallengeMallDto {
  public id?: string
  public name?: TranslationDto

  public static create(data?: Partial<ChallengeMallDto>): ChallengeMallDto {
    const instance = new ChallengeMallDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * Option gian hàng để chọn cho Milestone / Reward Option, load theo Mall.
 *
 * DB tách hai bảng liên kết khác nhau nên phải phân biệt được nguồn:
 *   `isOffline = false` → FK `Tenant`       (ChallengeMilestoneTenant)
 *   `isOffline = true`  → FK `OfflineTenant` (ChallengeMilestoneOfflineTenant,
 *                                             ChallengeRewardOptionOfflineTenant)
 */
export class ChallengeTenantOptionDto {
  public id?: string
  public name?: TranslationDto
  public mallId?: string
  public isOffline?: boolean

  public static create(data?: Partial<ChallengeTenantOptionDto>): ChallengeTenantOptionDto {
    const instance = new ChallengeTenantOptionDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * Bảng `Challenge` + `ChallengeTranslation` + `ChallengeMall`.
 *
 * API #9  `GET /challenges`      — list (row rút gọn, 9 field)
 * API #10 `GET /challenges/:id`  — detail (đầy đủ + milestones + rewardTiers)
 *
 * `name` / `description` giữ dạng `TranslationDto` cho cả 2 API; lớp service dựng lại
 * bằng `localizedToTranslation()` (API #9) hoặc `toTranslation()` (API #10) —
 * xem `stamp-collection.mapper.ts`.
 */
export class ChallengeDto extends BaseDto {
  public name?: TranslationDto
  public description?: TranslationDto
  /** FR-02-BR-02 — chọn 1 hoặc nhiều Mall (`ChallengeMall`). */
  public malls?: ChallengeMallDto[]
  public challengeImageId?: string
  /**
   * ✅ B5 ĐÃ ĐÓNG (response thật 2026-09-10): cả API #9 và #10 đều trả object file kèm `url`.
   *
   * ⚠️ Key là **`image`**, không phải `challengeImage` — không đối xứng với tên
   * `challengeImageId` bên cạnh, và cũng không đối xứng với `rewardBannerImage` (key này thì
   * lại khớp `rewardBannerImageId`). Đừng "sửa cho đồng bộ" nếu không đổi cả BE.
   */
  public challengeImageUrl?: string
  public rewardBannerImageId?: string
  /** ✅ API #10 — đọc `raw.rewardBannerImage?.url`. Chỉ có ở detail, không có ở list. */
  public rewardBannerImageUrl?: string
  /** FR-02-BR-03 — Milestones must be completed in sequence. */
  public sequentialMilestones?: boolean
  /** ISO Date string — `timestamp with timezone`. ✅ Cả 6 mốc khớp API #10/#11/#12. */
  public displayStartAt?: string
  public displayEndAt?: string
  /** ✅ B1 ĐÃ ĐÓNG — cả API #9 (list) và #10 (detail) đều trả 2 mốc này. */
  public challengeStartAt?: string
  public challengeEndAt?: string
  public redemptionStartAt?: string
  public redemptionEndAt?: string
  /**
   * FR-01-BR-02 — BE trả về, FE chỉ render.
   *
   * ⚠️ Swagger 2026-08-25: API #10/#11/#12 KHÔNG còn trả field này — thay bằng
   * `timePhase` + `lifecycleStatus`. Field này giờ là **giá trị bắc cầu do FE tự tính**
   * từ `timePhase` (xem `toBridgedStatus()` trong `stamp-collection.mapper.ts`) để chip cũ
   * và `canDeleteChallenge()` không phải sửa ngay. API #9 (List) vẫn khai `any`, chưa rõ có
   * đổi theo model 2 trục hay không.
   */
  public status?: ChallengeStatus
  /** API #10/#11/#12 — 2 trục thật, dùng khi cần hiển thị đúng cả `HIDDEN`/`OUT_OF_STOCK`. */
  public timePhase?: ChallengeTimePhase
  public lifecycleStatus?: ChallengeLifecycleStatus
  /** API #10/#11/#12 — thay `canEdit`/`canDelete` cũ. Chỉ có ở detail, không có ở List (#9). */
  public capabilities?: ChallengeCapabilitiesDto
  /** FR-01-BR-07 — công tắc Show/Hide trên Customer App. */
  public isVisible?: boolean
  /**
   * FR-01-BR-02 / FR-07-BR-04 — tổng quà chưa claim, dùng để hiểu lý do Ended.
   *
   * ✅ Swagger 2026-08-25: API #10 (detail) **đã trả** field này với tên `rewardRemaining`
   * — không cần workaround `notClaimed` của API #4 nữa (chỉ áp dụng cho detail; List #9
   * vẫn khai `any`, chưa rõ có trả field này không).
   */
  public remainingRewards?: number
  /**
   * FK `ChallengeConfiguration` — config **tại thời điểm tạo**, không phải config
   * hiện hành. ✅ API #10 có trả field này.
   */
  public challengeConfigurationId?: string
  /**
   * ⚠️ BE-MISSING B3: API #10 **không embed** object này (chỉ có id ở trên).
   * Luôn `undefined` ⇒ màn Edit đang phải dùng config hiện hành để validate limit.
   */
  public challengeConfiguration?: ChallengeConfigDto
  public milestones?: ChallengeMilestoneDto[]
  public rewardTiers?: ChallengeRewardTierDto[]
  /**
   * ⚠️ ĐÍNH CHÍNH bằng response thật 2026-09-10: BE **không trả** `canEdit`/`canDelete` ở
   * bất kỳ API nào — `API Design.md` mô tả sai. Cả #9 và #10 đều trả `capabilities`.
   *
   * `canEdit` giờ do `toChallengeRow()` điền từ `capabilities.canUpdate` (cùng nguồn với
   * `EditChallengeComponent.readonly`). `canDelete` **không có cờ tương ứng** trong
   * `capabilities` ⇒ luôn `undefined`, `ChallengeItemComponent` rơi về
   * `canDeleteChallenge(permission, status)` — FR-01-BR-06 chỉ cho xoá `PLANNED`.
   */
  public canEdit?: boolean
  public canDelete?: boolean

  public static create(data?: Partial<ChallengeDto>): ChallengeDto {
    const instance = new ChallengeDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-01-BR-03 — tham số lọc danh sách Challenge (API #9).
 * ✅ `search` / `mallId` / `status` / `limit` / `offset` khớp đúng tên BE.
 */
export class FindChallengeDto {
  public mallId?: string
  public status?: ChallengeStatus
  public search?: string
  /** API #9 hỗ trợ `from`/`to` — UI list hiện chưa có filter khoảng ngày. */
  public fromDate?: string
  public toDate?: string
  public page?: number
  public pageSize?: number

  public static create(data?: Partial<FindChallengeDto>): FindChallengeDto {
    const instance = new FindChallengeDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * Payload Create (API #11) / Update (API #12).
 *
 * Giữ `name`/`description` dạng `TranslationDto` cho form; `AdminChallengeService`
 * chuyển sang `translations[{languageCode,name,description}]` khi gửi đi.
 *
 * ⚠️ API #12: `milestones[].id`, `rewardTiers[].id`, `options[].id` là **optional** —
 * có id = update, không id = tạo mới. Nếu drop id khi Save thì BE xoá và tạo lại
 * ⇒ mất toàn bộ Reward Code đã phát. `edit-challenge.component.ts` phải giữ nguyên id
 * lấy từ API #10.
 */
export class SaveChallengeDto {
  public name: TranslationDto
  public description: TranslationDto
  public mallIds: string[]
  public challengeImageId: string
  public rewardBannerImageId: string
  public sequentialMilestones: boolean
  /**
   * `Challenge.isVisible` — điều khiển Display status trên form Create/Edit.
   *
   * ⚠️ Swagger 2026-08-25: body API #11/#12 **không còn field này**. Giữ lại trong DTO chỉ
   * để form bind 2 chiều (`[(checked)]="challenge.isVisible"`); `AdminChallengeService` đọc
   * giá trị này ở tầng gọi API để quyết định có cần gọi thêm API #13 (`PATCH .../visibility`)
   * sau khi Create/Update hay không — KHÔNG gửi trong body Create/Update nữa.
   */
  public isVisible: boolean
  public displayStartAt: string
  public displayEndAt: string
  public challengeStartAt: string
  public challengeEndAt: string
  public redemptionStartAt: string
  public redemptionEndAt: string
  public milestones: ChallengeMilestoneDto[]
  public rewardTiers: ChallengeRewardTierDto[]

  public static create(data?: Partial<SaveChallengeDto>): SaveChallengeDto {
    const instance = new SaveChallengeDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}
